import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import type { PremioPublic, RaspaditaMecanica } from "@juegossc/shared";
import { RuletaView } from "../../ruleta/RuletaView";
import { RaspaditaPreview } from "./RaspaditaPreview";
import { MemoriaBoard } from "../../memoria/MemoriaBoard";
import { VirtualScreen } from "./VirtualScreen";
import { buildBackgroundStyle } from "../../../hooks/useBackgroundStyle";

// Preview FIEL de la pantalla DENTRO de un juego (lo que ve el cliente al
// entrar): el header con Atrás + la campaña (alto/posición que se ajustan en
// Distribución › Dentro del juego) + el cuerpo del juego. Un selector arriba
// cambia entre Ruleta / Raspadita / Memoria — el HEADER (la campaña) es igual
// en los 3, así que cualquiera sirve para ver cómo queda la campaña; el cuerpo
// muestra cada juego. Mismo lienzo (VirtualScreen) que la pantalla real. Solo
// visual (§8 intacto).
//
type Juego = "ruleta" | "raspadita" | "memoria";
const JUEGOS: { key: Juego; label: string; titulo: string }[] = [
  { key: "ruleta", label: "Ruleta", titulo: "RULETA" },
  { key: "raspadita", label: "Raspadita", titulo: "RASPADITA" },
  { key: "memoria", label: "Memoria", titulo: "MEMORIA" },
];

export function JuegoPreview({
  juego: juegoProp,
  showSelector = true,
  bgUrl,
  campana,
  campanaJuegoHeightPx,
  campanaJuegoTopMarginPx,
  premios,
  colorBorde,
  colorCentro,
  grosorDivisoria,
  textAlign,
  juegoVerticalPosPct = 50,
  raspaditaMecanica,
  raspaditaIconUrl,
  raspaditaPanelW,
  raspaditaPanelH,
  memoriaImagenes,
  memoriaCardSize,
  canvasDprMax = 2,
  ruletaWheelSize,
  fontHeading,
  fontDisplay,
}: {
  /** Juego controlado desde afuera. Si se pasa, se ignora el selector
   *  interno — lo usa Mi pantalla › Juegos, donde la sub-pestaña ya elige
   *  el juego. Si se omite, el selector interno manda (Distribución). */
  juego?: Juego;
  /** Mostrar el selector Ruleta/Raspadita/Memoria. true en Distribución
   *  (no hay otro selector); false en Mi pantalla › Juegos (la sub-pestaña
   *  ya selecciona, sino se duplica el control). */
  showSelector?: boolean;
  bgUrl: string | null;
  campana: { nombre_display: string; imagen_url: string } | null;
  campanaJuegoHeightPx: number;
  campanaJuegoTopMarginPx: number;
  premios: PremioPublic[];
  colorBorde: string;
  colorCentro: string;
  grosorDivisoria: number;
  textAlign: "left" | "center" | "right";
  juegoVerticalPosPct?: number;
  raspaditaMecanica: RaspaditaMecanica;
  raspaditaIconUrl: string | null;
  /** Migration 039: tamaño del panel de raspadita (configurable). undefined =
   *  default de raspaditaSizes. */
  raspaditaPanelW?: number;
  raspaditaPanelH?: number;
  memoriaImagenes: Array<string | null>;
  /** Migration 039: lado de las cartas de memoria (configurable). */
  memoriaCardSize?: number;
  /** Migration 040: cap de DPR del canvas de la rueda en el preview. */
  canvasDprMax?: number;
  /** Migration 044: tamaño TARGET de la rueda (se clampea al lienzo). */
  ruletaWheelSize?: number;
  fontHeading: string;
  fontDisplay: string;
}) {
  const [juegoState, setJuego] = useState<Juego>("ruleta");
  // Controlado por prop si viene; sino, estado interno del selector.
  const juego = juegoProp ?? juegoState;
  const titulo = JUEGOS.find((j) => j.key === juego)?.titulo ?? "JUEGO";
  const bgStyle = buildBackgroundStyle(
    { bg_portrait_url: bgUrl, bg_landscape_url: null },
    false,
  );
  // Migration 037: posición vertical (espaciadores flex) para raspadita/memoria,
  // igual que RuletaView -> el preview responde al deslizador.
  const topGrow = Math.max(0, Math.min(100, juegoVerticalPosPct));
  const bottomGrow = 100 - topGrow;
  // Grid estático del tablero de memoria para el preview: cada imagen 2 veces,
  // patrón fijo, todas reveladas. Mismo MemoriaBoard que la pantalla real.
  const memoriaPreviewGrid = [0, 3, 1, 4, 2, 5, 5, 0, 4, 1, 3, 2].map(
    (imagen_idx, id) => ({ id, imagen_idx, estado: "revelada" as const }),
  );
  return (
    <div
      className="flex h-full w-full flex-col gap-2"
      style={
        {
          ["--font-heading"]: `"${fontHeading}", "Nunito", sans-serif`,
          ["--font-display"]: `"${fontDisplay}", "Nunito", sans-serif`,
        } as React.CSSProperties
      }
    >
      {/* Selector de juego: el header de la campaña es igual en los 3; el
          cuerpo cambia. Se oculta cuando el juego viene controlado de afuera
          (Mi pantalla › Juegos, donde la sub-pestaña ya selecciona). */}
      {showSelector && (
        <div className="flex shrink-0 gap-1 rounded-lg bg-white/5 p-1 text-xs">
          {JUEGOS.map((j) => (
            <button
              key={j.key}
              type="button"
              onClick={() => setJuego(j.key)}
              className={`flex-1 rounded-md px-2 py-1.5 font-medium transition ${
                juego === j.key
                  ? "bg-[#a78bfa]/20 text-white"
                  : "text-white/60 hover:bg-white/5 hover:text-white"
              }`}
            >
              {j.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex min-h-0 flex-1 items-center justify-center">
        <VirtualScreen bgStyle={bgStyle}>
          {juego === "ruleta" ? (
            // MISMO componente que la pantalla EN VIVO (routes/Ruleta.tsx via
            // FitScreen) sobre el MISMO lienzo -> el preview coincide 100% con
            // la pantalla real a cualquier resolucion (margenes, proporciones,
            // tamaño de la rueda, todo igual).
            <RuletaView
              preview
              campana={campana}
              campanaHeightPx={campanaJuegoHeightPx}
              campanaTopMarginPx={campanaJuegoTopMarginPx}
              premios={premios}
              colorBorde={colorBorde}
              colorCentro={colorCentro}
              grosorDivisoria={grosorDivisoria}
              textAlign={textAlign}
              verticalPosPct={juegoVerticalPosPct}
              dprMax={canvasDprMax}
              wheelSize={ruletaWheelSize}
              spinning={false}
              targetIndex={-1}
            />
          ) : (
            // Memoria usa MemoriaBoard (compartido con la pantalla real).
            // Raspadita usa el mock RaspaditaPreview, pero con las MISMAS medidas
            // del lienzo (raspaditaSizes.ts) que su pantalla en vivo -> coinciden
            // en tamaño/posición. El header de campaña es comun a los dos.
            <div className="app-mode flex h-full w-full flex-col">
              <header className="flex items-center justify-between gap-4 p-4">
                <div className="flex shrink-0 items-center gap-2 rounded-lg bg-white/10 px-4 py-2">
                  <ArrowLeft className="h-5 w-5" />
                  Atrás
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-center">
                  {campana?.imagen_url ? (
                    <img
                      src={campana.imagen_url}
                      alt={campana.nombre_display}
                      style={{
                        height: `${campanaJuegoHeightPx}px`,
                        marginTop: `${campanaJuegoTopMarginPx}px`,
                      }}
                      className="w-auto max-w-full shrink-0 object-contain drop-shadow-lg"
                    />
                  ) : (
                    <h1 className="font-heading text-xl font-bold opacity-80">
                      {titulo}
                    </h1>
                  )}
                </div>
                <div className="w-20 shrink-0" />
              </header>

              <section className="flex flex-1 flex-col items-center p-4">
                <div aria-hidden style={{ flex: `${topGrow} 1 0` }} />
                <div className="flex shrink-0 flex-col items-center gap-6">
                  {juego === "raspadita" && (
                    <RaspaditaPreview
                      mecanica={raspaditaMecanica}
                      iconUrl={raspaditaIconUrl}
                      panelW={raspaditaPanelW}
                      panelH={raspaditaPanelH}
                    />
                  )}
                  {juego === "memoria" && (
                    <MemoriaBoard
                      tiempoDisplay={2}
                      mostrarMemorizar
                      grid={memoriaPreviewGrid}
                      imagenesUrl={memoriaImagenes}
                      clickeable={false}
                      cardSize={memoriaCardSize}
                    />
                  )}
                </div>
                <div aria-hidden style={{ flex: `${bottomGrow} 1 0` }} />
              </section>
            </div>
          )}
        </VirtualScreen>
      </div>
    </div>
  );
}
